import React, { useCallback, useMemo } from 'react';
import { List } from '@mui/material';
import ExportListItem from './ExportListItem';
import { ExportVideoTypes } from '../../constants/ExportVideoTypes';
import AppConstants from '../../App/AppConstants';
import { secondsToVerboseText } from '../../utilities/timeUtilities';
import AddVideoCreditsDialog from './AddVideoCreditsDialog';

/**
 * Export resolution configurations
 */
const EXPORT_RESOLUTIONS = {
    '4K': { width: 3840, height: 2160, minRecommended: { width: 1920, height: 1080 } },
    '1080p': { width: 1920, height: 1080, minRecommended: { width: 1280, height: 720 } },
    '720p': { width: 1280, height: 720, minRecommended: { width: 854, height: 480 } }
};

/**
 * Helper function to get resolution warning for low-res backgrounds
 */
const getResolutionWarning = (exportType, backgroundDimensions, hasCredits) => {
    if (!hasCredits || !backgroundDimensions || !backgroundDimensions.width || !backgroundDimensions.height) {
        return null;
    }

    let exportResolution;
    let exportName;

    // Determine export resolution based on type
    if (exportType === ExportVideoTypes.FOURK_VIDEO) {
        exportResolution = EXPORT_RESOLUTIONS['4K'];
        exportName = '4K';
    } else if (exportType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE) {
        exportResolution = EXPORT_RESOLUTIONS['1080p'];
        exportName = '1080p';
    } else if (exportType === AppConstants.HOBBYIST_SUBSCRIPTION_TYPE || exportType === AppConstants.FREE_SUBSCRIPTION_TYPE) {
        exportResolution = EXPORT_RESOLUTIONS['720p'];
        exportName = '720p';
    } else {
        return null;
    }

    const { width: bgWidth, height: bgHeight } = backgroundDimensions;
    const { width: minWidth, height: minHeight } = exportResolution.minRecommended;

    // Check if background resolution is below recommended minimum
    if (bgWidth < minWidth || bgHeight < minHeight) {
        return `The selected background is ${bgWidth}x${bgHeight}. A background of at least ${minWidth}x${minHeight} is recommended for a high-quality ${exportName} export.`;
    }

    return null;
};

/**
 * Helper function to determine warning text based on subscription type and duration limits
 */
const getWarningText = (subscriptionType, duration, credits, accountDetails, maxValues, isFor4K = false) => {
    const isUnlimited = subscriptionType === AppConstants.LIMITLESS_SUBSCRIPTION_TYPE;
    const isPro = subscriptionType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE;
    const isSingle = !isPro && credits > 0;
    
    const maxDuration = isFor4K ? accountDetails.maxFourKDuration : accountDetails.maxDuration;
    const maxVideoDuration = isFor4K ? accountDetails.maxFourKDuration : maxValues.maxVideoDuration;
    
    if (isUnlimited) {
        if (duration > maxDuration) {
            return `The first ${secondsToVerboseText(maxDuration)} of audio will be exported.`;
        }
    } else if (isPro) {
        const { missingCredits } = calculateMissingAndRequiredCredits(
            duration, 
            accountDetails.maxDuration, 
            maxVideoDuration, 
            credits
        );
        
        if (missingCredits > 0) {
            return `The first ${secondsToVerboseText(accountDetails.maxDuration * credits)} of audio will be exported. Add credits to export longer audio.`;
        } else if (duration > maxVideoDuration) {
            return `The first ${secondsToVerboseText(maxVideoDuration)} of audio will be exported.`;
        }
    } else if (isSingle) {
        if (duration > maxDuration) {
            return `The first ${secondsToVerboseText(maxDuration)} of audio will be exported. Upgrade to export longer audio.`;
        }
    }
    
    return null;
};

const calculateMissingAndRequiredCredits = (duration, maxDuration, maxVideoDuration, creditsAmount) => {
    let durationCeiling = Math.ceil(duration / maxDuration) * maxDuration;
    durationCeiling = durationCeiling > maxVideoDuration ? maxVideoDuration : durationCeiling;
    const requiredCredits = durationCeiling / maxDuration;
    const missingCredits = requiredCredits > creditsAmount ? requiredCredits - creditsAmount : 0;

    return { requiredCredits, missingCredits };
};

const determinateOptionContent = (duration, creditsAmount, accountDetails, maxValues) => {
    const isUnlimited = accountDetails.subscriptionType === AppConstants.LIMITLESS_SUBSCRIPTION_TYPE;
    const isPro = accountDetails.subscriptionType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE;
    const isSingle = !isPro && creditsAmount > 0;
    const disabled = !isUnlimited && creditsAmount < 1;

    const warningText = getWarningText(
        accountDetails.subscriptionType,
        duration,
        creditsAmount,
        accountDetails,
        maxValues,
        false
    );

    let secondaryText = '';
    let missingCredits = 0;

    if (isUnlimited) {
        secondaryText = 'Unlimited';
    } else if (isPro) {
        const credits = calculateMissingAndRequiredCredits(duration, accountDetails.maxDuration, maxValues.maxVideoDuration, creditsAmount);
        missingCredits = credits.missingCredits;

        if (creditsAmount < 1) {
            secondaryText = '0 credits remaining';
        } else {
            secondaryText = `Use ${credits.requiredCredits - credits.missingCredits} credit${credits.requiredCredits !== 1 ? 's' : ''} (${creditsAmount} remaining)`;
        }
    } else if (isSingle) {
        secondaryText = `Use 1 credit (${creditsAmount} remaining)`;
    } else {
        secondaryText = 'Upgrade required';
    }

    return {
        warningText,
        secondaryText,
        missingCredits,
        disabled,
        needsUpgrade: !isUnlimited && !isPro && !isSingle,
        needsCredits: isPro && missingCredits > 0
    };
};

const get4KOptionContent = (duration, accountDetails) => {
    const isUnlimited = accountDetails.subscriptionType === AppConstants.LIMITLESS_SUBSCRIPTION_TYPE;
    const isPro = accountDetails.subscriptionType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE;
    const isSingle = !isUnlimited && !isPro && accountDetails.fourKCredits > 0;
    const disabled = !isUnlimited && accountDetails.fourKCredits < 1;

    const warningText = getWarningText(
        accountDetails.subscriptionType,
        duration,
        accountDetails.fourKCredits,
        accountDetails,
        { maxVideoDuration: accountDetails.maxFourKDuration }, // For 4K, maxVideoDuration is maxFourKDuration
        true
    );

    let secondaryText = '';
    let missingCredits = 0;

    if (isUnlimited) {
        secondaryText = 'Unlimited';
    } else if (isPro) {
        const credits = calculateMissingAndRequiredCredits(duration, accountDetails.maxDuration, accountDetails.maxFourKDuration, accountDetails.fourKCredits);
        missingCredits = credits.missingCredits;

        secondaryText = accountDetails.fourKCredits < 1
            ? '0 credits remaining'
            : `Use ${credits.requiredCredits - credits.missingCredits} credit${credits.requiredCredits !== 1 ? 's' : ''} (${accountDetails.fourKCredits} remaining)`;
    } else if (isSingle) {
        secondaryText = `Use 1 credit (${accountDetails.fourKCredits} remaining)`;
    }

    return {
        warningText,
        secondaryText,
        missingCredits,
        disabled,
        needsUpgrade: !isUnlimited && !isPro && !isSingle,
        needsCredits: isPro && missingCredits > 0
    };
};

const ExportOptionsList = React.memo(({
    duration,
    videoType,
    accountDetails,
    maxValues,
    addCreditsOpen,
    onVideoTypeChange,
    onUpgradeClick,
    onCreditsClick,
    onAddCreditsClose,
    backgroundDimensions
}) => {
    // Precompute option contents to avoid recomputation across callbacks
    const optionContent4k = useMemo(() => get4KOptionContent(duration, accountDetails), [duration, accountDetails]);
    const optionContentPro = useMemo(
        () => determinateOptionContent(duration, accountDetails.professionalCredits, accountDetails, maxValues),
        [duration, accountDetails, maxValues]
    );

    // 4K Export Item
    const render4KItem = useCallback(() => {
        const isUnlimited = accountDetails.subscriptionType === AppConstants.LIMITLESS_SUBSCRIPTION_TYPE;
        const isPro = accountDetails.subscriptionType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE;
        const isSingle = !isPro && accountDetails.fourKCredits > 0;

        if (!isUnlimited && !isPro && !isSingle) {
            return null;
        }
        
        const optionContent = optionContent4k;
        const hasCredits = !optionContent.disabled;
        const resolutionWarning = getResolutionWarning(ExportVideoTypes.FOURK_VIDEO, backgroundDimensions, hasCredits);

        return (
            <ExportListItem
                videoType={ExportVideoTypes.FOURK_VIDEO}
                selectedVideoType={videoType}
                onSelect={() => onVideoTypeChange(ExportVideoTypes.FOURK_VIDEO)}
                disabled={optionContent.disabled}
                primary="4K 60fps"
                secondary={optionContent.secondaryText}
                warningText={optionContent.warningText}
                resolutionWarning={resolutionWarning}
                helpText={!optionContent.warningText && !resolutionWarning ? '4K exports take longer to complete and result in larger file sizes than 1080p exports.' : null}
                showCreditsButton={optionContent.needsCredits}
                showUpgradeButton={optionContent.needsUpgrade}
                onUpgradeClick={onUpgradeClick}
                onCreditsClick={onCreditsClick}
            />
        );
    }, [videoType, accountDetails, backgroundDimensions, optionContent4k, onVideoTypeChange, onUpgradeClick, onCreditsClick]);

    // Pro Export Item
    const renderProItem = useCallback(() => {
        const optionContent = optionContentPro;
        const hasCredits = !optionContent.disabled;
        const resolutionWarning = getResolutionWarning(AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE, backgroundDimensions, hasCredits);

        return (
            <ExportListItem
                videoType={AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE}
                selectedVideoType={videoType}
                onSelect={() => onVideoTypeChange(AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE)}
                disabled={optionContent.disabled}
                primary="1080p 60fps"
                secondary={optionContent.secondaryText}
                warningText={optionContent.warningText}
                resolutionWarning={resolutionWarning}
                showCreditsButton={optionContent.needsCredits}
                showUpgradeButton={optionContent.needsUpgrade}
                onUpgradeClick={onUpgradeClick}
                onCreditsClick={onCreditsClick}
            />
        );
    }, [videoType, accountDetails, backgroundDimensions, optionContentPro, onVideoTypeChange, onUpgradeClick, onCreditsClick]);

    // Hobbyist Export Item
    const renderHobbyistItem = useCallback(() => {
        if (accountDetails.hobbyistCredits < 1) {
            return null;
        }

        const optionContent = determinateOptionContent(duration, accountDetails.hobbyistCredits, accountDetails, maxValues);
        const hasCredits = !optionContent.disabled;
        const resolutionWarning = getResolutionWarning(AppConstants.HOBBYIST_SUBSCRIPTION_TYPE, backgroundDimensions, hasCredits);

        return (
            <ExportListItem
                videoType={AppConstants.HOBBYIST_SUBSCRIPTION_TYPE}
                selectedVideoType={videoType}
                onSelect={() => onVideoTypeChange(AppConstants.HOBBYIST_SUBSCRIPTION_TYPE)}
                disabled={optionContent.disabled}
                primary="720p 30fps"
                secondary={optionContent.secondaryText}
                warningText={optionContent.warningText}
                resolutionWarning={resolutionWarning}
                showUpgradeButton={optionContent.needsUpgrade}
                onUpgradeClick={onUpgradeClick}
            />
        );
    }, [duration, videoType, accountDetails, maxValues, backgroundDimensions, onVideoTypeChange, onUpgradeClick]);

    // Free Export Item
    const renderFreeItem = useCallback(() => {
        const isPro = accountDetails.subscriptionType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE;
        const isHobbyist = accountDetails.subscriptionType === AppConstants.HOBBYIST_SUBSCRIPTION_TYPE;
        const isUnlimited = accountDetails.subscriptionType === AppConstants.LIMITLESS_SUBSCRIPTION_TYPE;
        const exceedsMaxDuration = accountDetails.maxDuration < duration;

        if (isUnlimited) {
            return null;
        }

        const has4KCredits = accountDetails.fourKCredits > 0;
        const hasProCredits = accountDetails.professionalCredits > 0;
        const hasHobbyistCredits = accountDetails.hobbyistCredits > 0;
        const hasFreeCredits = accountDetails.freeCredits > 0;

        // Pro user can see and use free credits if there are no any other subscription credits.
        if ((isPro || isHobbyist) && (has4KCredits || hasProCredits || hasHobbyistCredits || !hasFreeCredits)) {
            return null;
        }

        const warningText = exceedsMaxDuration 
            ? `The first ${secondsToVerboseText(accountDetails.maxFreeDuration)} of audio will be exported. See options to export longer audio.`
            : null;

        const hasCredits = accountDetails.freeCredits > 0;
        const resolutionWarning = getResolutionWarning(AppConstants.FREE_SUBSCRIPTION_TYPE, backgroundDimensions, hasCredits);

        return (
            <ExportListItem
                videoType={AppConstants.FREE_SUBSCRIPTION_TYPE}
                selectedVideoType={videoType}
                onSelect={() => onVideoTypeChange(AppConstants.FREE_SUBSCRIPTION_TYPE)}
                disabled={accountDetails.freeCredits <= 0}
                primary="720p 30fps"
                secondary={accountDetails.freeCredits > 0 
                    ? `Use 1 credit (${accountDetails.freeCredits} remaining)`
                    : 'Upgrade required'
                }
                warningText={warningText}
                resolutionWarning={resolutionWarning}
                showUpgradeButton={accountDetails.freeCredits <= 0}
                onUpgradeClick={onUpgradeClick}
            />
        );
    }, [duration, videoType, accountDetails, backgroundDimensions, onVideoTypeChange, onUpgradeClick]);

    // Test Export Item
    const renderTestItem = useCallback(() => {
        if (accountDetails.subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE) {
            return null;
        }

        return (
            <ExportListItem
                videoType={ExportVideoTypes.TEST_VIDEO}
                selectedVideoType={videoType}
                onSelect={() => onVideoTypeChange(ExportVideoTypes.TEST_VIDEO)}
                disabled={false}
                primary="Test"
                secondary="Unlimited"
                helpText="Export the first 30 seconds to make sure your final video looks correct."
            />
        );
    }, [videoType, accountDetails, onVideoTypeChange]);

    const optionContent = determinateOptionContent(duration, accountDetails.professionalCredits, accountDetails, maxValues);

    return (
        <>
            <List disablePadding>
                {render4KItem()}
                {renderProItem()}
                {renderHobbyistItem()}
                {renderFreeItem()}
                {renderTestItem()}
            </List>
            {addCreditsOpen && (
                <AddVideoCreditsDialog
                    open={addCreditsOpen}
                    onClose={onAddCreditsClose}
                    location='export dialog'
                    defaultQuantity={optionContent.missingCredits}
                />
            )}
        </>
    );
});

export default ExportOptionsList;